import { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ArrowRight, PackageOpen } from 'lucide-react-native';

import { Badge, Button, Sheet, TextField, Select, type SelectOption } from '@/components/ui';
import { registrarSalida } from '@/services/firebase/ventas';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { AREAS, type AreaId, areaNombre } from '@/utils/areas';
import type { ProductoVenta, InsumoInterno } from '@/types/domain';

type Producto = ProductoVenta | InsumoInterno;
type Coleccion = 'productos_venta' | 'insumos_internos';

interface Props {
  visible: boolean;
  onClose: () => void;
  producto: Producto | null;
  coleccion?: Coleccion;
}

const DESTINOS: SelectOption[] = AREAS.filter((a) => a.id !== 'otra').map((a) => ({
  value: a.id,
  label: a.nombre,
  hint: a.descripcion,
}));

// Mantengo el nombre del archivo `VenderSheet.tsx` por compatibilidad con imports,
// pero esto ya es "Salida de almacén" — no hay precio ni movimiento contable.
export function VenderSheet({ visible, onClose, producto, coleccion = 'productos_venta' }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const [cantidad, setCantidad] = useState('1');
  const [destino, setDestino] = useState<AreaId>('recepcion');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !producto) return;
    setCantidad('1');
    setDestino('recepcion');
    setMotivo('');
    setError(null);
    setSaving(false);
  }, [visible, producto]);

  if (!producto) return null;

  const cantidadN = Number(cantidad);
  const stockTras = (producto.stock ?? 0) - (Number.isFinite(cantidadN) ? cantidadN : 0);
  const sinStockSuficiente = !Number.isFinite(cantidadN) || cantidadN <= 0 || cantidadN > (producto.stock ?? 0);

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (!Number.isFinite(cantidadN) || cantidadN <= 0) return setError('Cantidad inválida.');
    if (cantidadN > (producto.stock ?? 0)) return setError(`Stock insuficiente. Quedan ${producto.stock ?? 0}.`);
    if (!destino) return setError('Elegí el destino.');

    setSaving(true);
    try {
      await registrarSalida({
        productoId: producto.id,
        coleccion,
        cantidad: cantidadN,
        destino,
        motivo: motivo.trim() || undefined,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo registrar la salida.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title={coleccion === 'productos_venta' ? 'Salida de farmacia' : 'Salida de almacén'}
      subtitle={`${producto.nombre} · ${producto.presentacion}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label="Registrar salida"
            iconLeft={<ArrowRight size={16} color={colors.white} />}
            onPress={onSubmit}
            loading={saving}
            disabled={sinStockSuficiente}
            variant="success"
          />
        </>
      }
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: radius['2xl'], backgroundColor: colors.crema[50] }}>
        <View
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            backgroundColor: colors.magenta[100],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PackageOpen size={20} color={colors.magenta[700]} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {producto.nombre}
          </Text>
          <Text style={{ fontSize: 12, color: colors.ink[500] }}>{producto.presentacion}</Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8 }}>STOCK</Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: producto.stockBajo ? colors.warning[600] : colors.success[600] }}>
            {producto.stock}
          </Text>
        </View>
      </View>

      {/* Destino + Cantidad en una fila */}
      <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <View style={{ flex: 1.4, minWidth: 200 }}>
          <Select
            label="Destino"
            value={destino}
            onChange={(v) => v && setDestino(v as AreaId)}
            options={DESTINOS}
          />
        </View>
        <View style={{ flex: 1, minWidth: 180 }}>
          <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700], marginBottom: 6 }}>
            Cantidad
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Pressable
              onPress={() => setCantidad(String(Math.max(1, cantidadN - 1)))}
              style={({ hovered }) => ({
                width: 44,
                height: 50,
                borderRadius: radius.xl,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
                borderWidth: 1.5,
                borderColor: colors.ink[200],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ fontSize: 22, color: colors.ink[700], fontWeight: typography.weights.bold }}>−</Text>
            </Pressable>
            <TextField containerStyle={{ flex: 1 }} value={cantidad} onChangeText={setCantidad} keyboardType="numeric" />
            <Pressable
              onPress={() => setCantidad(String(Math.min((producto.stock ?? 0), cantidadN + 1)))}
              style={({ hovered }) => ({
                width: 44,
                height: 50,
                borderRadius: radius.xl,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.magenta[200] : colors.magenta[100],
                borderWidth: 1.5,
                borderColor: colors.magenta[200],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ fontSize: 22, color: colors.magenta[700], fontWeight: typography.weights.bold }}>+</Text>
            </Pressable>
          </View>
        </View>
      </View>

      <TextField
        label="Motivo (opcional)"
        placeholder="Ej: para consulta de Mishi, vacuna paciente, etc."
        value={motivo}
        onChangeText={setMotivo}
        multiline
      />

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderRadius: radius.xl, backgroundColor: colors.success[50], gap: 8 }}>
        <View style={{ gap: 2, flex: 1 }}>
          <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.success[600], letterSpacing: 1, textTransform: 'uppercase' }}>
            Resumen
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {Number.isFinite(cantidadN) ? cantidadN : 0} × {producto.nombre}
          </Text>
          <Text style={{ fontSize: 12, color: colors.ink[500] }}>
            Va a: {areaNombre(destino)}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 2 }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 1, textTransform: 'uppercase' }}>
            Stock tras salida
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: stockTras <= 0 ? colors.danger[600] : stockTras <= (producto.stockMin ?? 0) ? colors.warning[600] : colors.ink[800] }}>
            {Number.isFinite(stockTras) ? stockTras : '—'}
          </Text>
          {stockTras <= 0 && Number.isFinite(stockTras) ? (
            <Badge label="Quedará agotado" tone="danger" />
          ) : stockTras <= (producto.stockMin ?? 0) ? (
            <Badge label="Quedará en bajo" tone="warning" />
          ) : null}
        </View>
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
